import React, { useState, createContext, useContext } from 'react';
import graphActions from '../../../../actions/graph';

const Context = createContext(undefined);

/**
 * Holds the context of the operator map element configuration
 */
const OperatorProvider = ({
    mapElement: initialMapElement = null,
    flowId,
    editingToken,
    authenticationToken,
    tenantId,
    initialOperationIndex = null,
    initialNavigationIndex = null,
    children,
    save,
    cancel,
    notifyError,
    notifySuccess,
}) => {
    const OPERATOR_SCREENS = {
        configuration: 'configuration',
        operationDetails: 'operationDetails',
        macroDetails: 'macroDetails',
        navigationDetails: 'navigationDetails',
    };

    const [mapElement, setMapElement] = useState(initialMapElement);
    const [currentScreen, setCurrentScreen] = useState(OPERATOR_SCREENS.configuration);
    const [selectedOperationIndex, setSelectedOperationIndex] = useState(initialOperationIndex);
    const [selectedNavigationIndex, setSelectedNavigationIndex] = useState(initialNavigationIndex);

    const onEditOperation = (operationIndex) => {
        setSelectedOperationIndex(operationIndex);
        if (mapElement.operations[operationIndex].macroElementToExecuteId !== null) {
            setCurrentScreen(OPERATOR_SCREENS.macroDetails);
        } else {
            setCurrentScreen(OPERATOR_SCREENS.operationDetails);
        }
    };

    const onDeleteOperation = (operationIndex) => {
        setMapElement({
            ...mapElement,
            operations: mapElement.operations.filter((_, index) => index !== operationIndex),
        });
    };

    const onEditNavigation = (navigationIndex) => {
        setSelectedNavigationIndex(navigationIndex);
        setCurrentScreen(OPERATOR_SCREENS.navigationDetails);
    };

    const onDeleteNavigation = (navigationIndex) => {
        setMapElement({
            ...mapElement,
            navigationOverrides: mapElement.navigationOverrides.filter(
                (_, index) => index !== navigationIndex,
            ),
        });
    };

    const saveMapElement = () => {
        // Set order before saving
        mapElement.operations.forEach((operation, index) => {
            operation.order = index;
        });

        save(mapElement, flowId, editingToken, tenantId, authenticationToken, cancel);
    };

    const cancelMapElement = () => {
        cancel(flowId);
        if (!mapElement?.dateCreated ?? false) {
            // This triggers a re-render of the entire Flow canvas
            // so that changes we have made to the map element appear in the canvas
            graphActions.get(flowId, tenantId, authenticationToken);
        }
    };

    const contextValue = {
        OPERATOR_SCREENS,
        authenticationToken,
        tenantId,
        flowId,
        editingToken,
        mapElement,
        currentScreen,
        selectedOperationIndex,
        selectedNavigationIndex,
        setCurrentScreen,
        setMapElement,
        saveMapElement,
        cancelMapElement,
        setSelectedOperationIndex,
        setSelectedNavigationIndex,
        onEditOperation,
        onDeleteOperation,
        onEditNavigation,
        onDeleteNavigation,
        notifyError,
        notifySuccess,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useOperator = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useOperator must be used within a OperatorProvider');
    }
    return context;
};

export { OperatorProvider, useOperator };
